import { useCallback, useEffect, useState, useMemo } from "react";
import { Package, Plus, Pencil, Archive, Trash2, HelpCircle, Copy } from "lucide-react";
import { GenericEntityWorkspacePage } from "./shared/GenericEntityWorkspacePage";
import { Modal } from "../primitives/Modal";
import { Drawer } from "../primitives/Drawer";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { Button } from "../primitives/Button";
import { Tabs } from "../primitives/Tabs";
import { IconButton } from "../primitives/IconButton";
import { listProducts, createProduct, updateProduct, deleteProduct, getProduct, getProductPricingModel, createProductPricingModel, getProductDetails, listCategories, getProductCategories, putProductCategories, updateProductInventory, type Product, type ProductType, type ProductStatus, type ProductUsage, type BundleBasePrice, BUNDLE_FAMILY_OPTIONS } from "../../api/catalog";
import { listSuppliers } from "../../api/suppliers";
import { cn } from "../utils/cn";
import type { EntityWorkspaceBoardConfig, EntityListItem } from "./shared/entityWorkspaceBoardConfigs";
import { ProductDetailsTab } from "../components/product/ProductDetailsTab";
import { ProductGeneralTab } from "../components/product/ProductGeneralTab";
import { ProductPricingTab } from "../components/product/ProductPricingTab";
import { ProductDeliverablesTab } from "../components/product/ProductDeliverablesTab";
import { ProductOrchestrationTab } from "../components/product/ProductOrchestrationTab";
import { ProductInternalCostsTab } from "../components/product/ProductInternalCostsTab";
import { ProductInventoryTab } from "../components/product/ProductInventoryTab";
import { ProductSalesTab } from "../components/product/ProductSalesTab";
import { ProductCategoriesTab } from "../components/product/ProductCategoriesTab";
import { ProductComponentsTab } from "../components/product/ProductComponentsTab";

type ProductFamily = "produits" | "services" | "licences" | "data_processing" | "bundles" | "consommables" | "all";

const PRODUCT_FAMILIES: { id: ProductFamily; label: string }[] = [
  { id: "all", label: "Tous" },
  { id: "produits", label: "Produits" },
  { id: "services", label: "Services" },
  { id: "data_processing", label: "Traitement de données" },
  { id: "licences", label: "Abonnements & Crédits" },
  { id: "consommables", label: "Consommables" },
  { id: "bundles", label: "Bundles" },
];

/** Map product_type → family for filtering (incl. data_processing, bundle, consommables). */
const productTypeToFamily: Record<string, ProductFamily> = {
  produit: "produits",
  service: "services",
  licence: "licences",
  consommable: "consommables",
  data_processing: "data_processing",
  bundle: "bundles",
};

/** Type affiché pour regroupement sidebar : on priorise le product_type. Consommable si type consommable, ou produit consommé en interne (migration 0061). */
function getEffectiveDisplayType(p: { product_type?: string; product_usage?: string }): string {
  const type = p.product_type ?? "produit";
  if (type === "consommable") return "consommable";
  if (type === "produit" && p.product_usage === "internal_consumption") return "consommable";
  return type;
}

/** Famille de filtre pour un produit (utilise le type affiché). */
function getFamilyForProduct(p: { product_type?: string; product_usage?: string }): ProductFamily {
  const effective = getEffectiveDisplayType(p);
  return effective === "consommable" ? "consommables" : (productTypeToFamily[effective] ?? "produits");
}

/** Types affichés dans le modal Créer/éditer — Bundle = offre structurée (règles de composition, mode de calcul). */
const MODAL_TYPE_OPTIONS: { value: ProductType; label: string }[] = [
  { value: "produit", label: "Produit" },
  { value: "service", label: "Service" },
  { value: "data_processing", label: "Traitement de données" },
  { value: "licence", label: "Licence" },
  { value: "consommable", label: "Consommable" },
  { value: "bundle", label: "Bundle" },
];

const TYPE_HELP =
  "Le type définit le comportement du produit dans le système : quels champs sont disponibles (inventaire, fournisseur, catégories) et comment il est facturé. " +
  "Matériel = bien physique avec possible suivi stock. Service = prestation. Abonnement = récurrent. Crédits XLHub = droits d’usage. Forfait = assemblage de produits. " +
  "Pour distinguer drone, pièce, accessoire, etc., utilisez les Catégories (section Classification), pas le type.";

const CATEGORY_HELP =
  "Les catégories servent à organiser le catalogue selon votre métier (ex. Drone, Pièce, Accessoire, Batterie, Structure). " +
  "Elles permettent de filtrer et regrouper les produits. Cette section n’apparaît que pour le type Matériel.";

/** Ordre affiché (types catalogue). */
const PRODUCT_TYPE_OPTIONS: { value: ProductType; label: string; family: ProductFamily; description: string }[] = [
  { value: "produit", label: "Produit", family: "produits", description: "Produits physiques." },
  { value: "service", label: "Service", family: "services", description: "Intervention terrain, mandat, acquisition." },
  { value: "data_processing", label: "Traitement de données", family: "data_processing", description: "Traitement, analyse, calcul (ex. orthomosaïque, NDVI, par superficie)." },
  { value: "licence", label: "Licence", family: "licences", description: "Abonnements, crédits." },
  { value: "consommable", label: "Consommable", family: "consommables", description: "Consommé en interne ou en bundle." },
  { value: "bundle", label: "Bundle", family: "bundles", description: "Offre regroupant plusieurs éléments vendables selon des règles (obligatoires, optionnels, quantités)." },
];

/** Ordre des catégories dans la barre latérale. */
const SIDEBAR_TYPE_ORDER: ProductType[] = ["produit", "service", "data_processing", "licence", "consommable", "bundle"];

/** Unités pour le prix de base du bundle (mode par unité). */
const BUNDLE_BASE_UNITS: { value: string; label: string }[] = [
  { value: "each", label: "Chaque" },
  { value: "acre", label: "Acre" },
  { value: "hectare", label: "Hectare" },
  { value: "project", label: "Projet" },
  { value: "hour", label: "Heure" },
];

const STATUS_OPTIONS: { value: ProductStatus; label: string }[] = [
  { value: "active", label: "Actif" },
  { value: "inactive", label: "Inactif" },
  { value: "archived", label: "Archivé" },
];

/** Mode de tarification (refonte : fixed | per_unit uniquement). */
type BillingChoice = "fixed" | "per_unit";
const BILLING_OPTIONS: { value: BillingChoice; label: string; description: string; mode: "fixed" | "per_unit"; defaultUnitKind: string }[] = [
  { value: "fixed", label: "Prix fixe", mode: "fixed", defaultUnitKind: "each", description: "Montant unique" },
  { value: "per_unit", label: "Par quantité", mode: "per_unit", defaultUnitKind: "each", description: "Prix × quantité (unité = params.unit)" },
];

function getBillingOptionsForType(productType: string): typeof BILLING_OPTIONS {
  return BILLING_OPTIONS;
}

function getDefaultBillingForType(productType: string): BillingChoice {
  if (productType === "data_processing") return "per_unit";
  return "fixed";
}

function normalizeUnitForType(productType: string, unitKind: string | null, howBilling: BillingChoice): string | null {
  if (howBilling === "fixed") return "each";
  const allowed = UNIT_OPTIONS_BY_TYPE[productType] ?? UNIT_OPTIONS_BY_TYPE.produit;
  if (unitKind && allowed.some((u) => u.value === unitKind)) return unitKind;
  return allowed[0]?.value ?? "each";
}

const TAX_CODE_OPTIONS: { value: string; label: string }[] = [
  { value: "OUT_OF_SCOPE", label: "Hors champ (0%)" },
  { value: "EXEMPT", label: "Exonéré (0%)" },
  { value: "ZERO_RATED", label: "Détaxé (0%)" },
  { value: "STANDARD_QC", label: "TPS/TVQ QC - 9,975 (14,975%)" },
];

/** Unité de vente (source de vérité = PricingModel.params.unit). Options par type. */
const UNIT_OPTIONS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  produit: [
    { value: "each", label: "Chaque" },
    { value: "acre", label: "Acre" },
    { value: "hectare", label: "Hectare" },
  ],
  service: [
    { value: "each", label: "Chaque" },
    { value: "hour", label: "Heure" },
    { value: "acre", label: "Acre" },
    { value: "hectare", label: "Hectare" },
    { value: "project", label: "Projet" },
  ],
  bundle: [
    { value: "each", label: "Chaque" },
    { value: "acre", label: "Acre" },
    { value: "hectare", label: "Hectare" },
    { value: "project", label: "Projet" },
  ],
  licence: [
    { value: "each", label: "Chaque" },
    { value: "month", label: "Mois" },
    { value: "year", label: "Année" },
    { value: "credit", label: "Crédit" },
  ],
  consommable: [{ value: "each", label: "Chaque" }],
  data_processing: [
    { value: "hectare", label: "Hectare" },
    { value: "acre", label: "Acre" },
    { value: "each", label: "Chaque" },
    { value: "project", label: "Projet" },
  ],
};

export function CatalogPage({ userName = "Utilisateur" }: { userName?: string }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFamily, setActiveFamily] = useState<ProductFamily>("all");
  const [open, setOpen] = useState(false);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<"general" | "pricing" | "deliverables" | "process" | "internal_costs" | "inventory" | "sales" | "categories" | "components">("general");
  const [form, setForm] = useState({
    code: "",
    name: "",
    product_type: "produit" as ProductType,
    product_usage: "sold" as ProductUsage,
    status: "active" as ProductStatus,
    description_md: "",
    unit_kind: "each" as string,
    tags: [] as string[],
    howBilling: "fixed" as BillingChoice,
    tax_code: "STANDARD_QC",
    tax_profile: "qc_standard" as string,
    document_detail_template_md: "",
    service_metadata: null as Record<string, unknown> | null,
    category_ids: [] as string[],
    minimum_charge: null as number | null,
    bundle_base_price: null as BundleBasePrice | null,
    bundle_family: "" as string,
    ops_family: "" as string,
  });
  const [categoriesList, setCategoriesList] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [suppliersList, setSuppliersList] = useState<{ id: string; name: string }[]>([]);
  const [editButtonLoading, setEditButtonLoading] = useState(false);
  const [updateSaving, setUpdateSaving] = useState(false);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [selectedTagFilter, setSelectedTagFilter] = useState<string | null>(null);

  const distinctTags = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => (p.tags || []).forEach((t) => t?.trim() && set.add(t.trim())));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [products]);

  const distinctOpsFamilies = useMemo(() => {
    const set = new Set<string>();
    products.forEach((p) => {
      const v = (p as Product & { ops_family?: string | null }).ops_family;
      if (v?.trim()) set.add(v.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [products]);

  // Réinitialiser l’onglet si on affiche un produit sans onglet Approvisionnement
  // Filter products based on family (consommables = product_type consommable ou product_usage internal_consumption)
  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (activeFamily !== "all") {
      filtered = filtered.filter((p) => getFamilyForProduct(p) === activeFamily);
    }

    return filtered;
  }, [products, activeFamily]);

  const filteredByTag = useMemo(() => {
    if (!selectedTagFilter) return filteredProducts;
    return filteredProducts.filter((p) => (p.tags || []).includes(selectedTagFilter));
  }, [filteredProducts, selectedTagFilter]);

  const selectedProduct = useMemo(() => products.find((p) => p.id === selectedId), [products, selectedId]);

  // Réinitialiser l’onglet si on affiche un produit sans onglet Approvisionnement
  useEffect(() => {
    if (
      selectedProduct &&
      (selectedProduct.product_type === "service" || selectedProduct.product_type === "data_processing") &&
      activeTab === "inventory"
    ) {
      setActiveTab("general");
    }
  }, [selectedProduct?.id, selectedProduct?.product_type, activeTab]);

  // Load categories and suppliers when create modal or edit drawer opens
  useEffect(() => {
    if (!open && !editProduct) return;
    let cancelled = false;
    Promise.all([
      listCategories({ kind: "parts" }).catch(() => []),
      listSuppliers({ status: "active", page_size: 200 }).catch(() => []),
    ])
      .then(([cats, supps]) => {
        if (!cancelled) {
          setCategoriesList(cats.map((c) => ({ id: c.id, name: c.name, slug: c.slug })));
          setSuppliersList(supps.map((s) => ({ id: s.id, name: s.name })));
        }
      });
    return () => { cancelled = true; };
  }, [open, editProduct]);

  const PAGE_SIZE = 1000;
  const loadAllProducts = useCallback(async (): Promise<Product[]> => {
    const first = await listProducts({ page: 1, page_size: PAGE_SIZE, list: true });
    const all: Product[] = [...(first.items || [])];
    const total = first.meta?.total ?? 0;
    const restPages = Math.ceil(total / PAGE_SIZE) - 1;
    if (restPages > 0) {
      const rest = await Promise.all(
        Array.from({ length: restPages }, (_, i) =>
          listProducts({ page: i + 2, page_size: PAGE_SIZE, list: true })
        )
      );
      rest.forEach((r) => all.push(...(r.items || [])));
    }
    return all.slice(0, total);
  }, []);

  // Load products (chunked pour dépasser la limite API 1000)
  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    loadAllProducts()
      .then((all) => {
        if (!mounted) return;
        setProducts(all);
        if (all.length > 0) setSelectedId((prev) => prev || all[0].id);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load products:", error);
        if (mounted) {
          alert(`Erreur lors du chargement des produits: ${error.message || error}`);
          setIsLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [loadAllProducts]);

  const getProductFamilyLabel = (productType: ProductType): string => {
    switch (productType) {
      case "produit":
        return "Produit";
      case "service":
        return "Service";
      case "licence":
        return "Licence";
      case "consommable":
        return "Consommable";
      case "data_processing":
        return "Traitement de données";
      case "bundle":
        return "Bundle";
      default:
        return "Autres";
    }
  };

  const getProductFamilyColor = (productType: ProductType): string => {
    switch (productType) {
      case "produit":
        return "bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-400";
      case "service":
        return "bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-400";
      case "licence":
        return "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400";
      case "consommable":
        return "bg-gray-500/10 border-gray-500/30 text-gray-700 dark:text-gray-400";
      case "data_processing":
        return "bg-violet-500/10 border-violet-500/30 text-violet-700 dark:text-violet-400";
      case "bundle":
        return "bg-teal-500/10 border-teal-500/30 text-teal-700 dark:text-teal-400";
      default:
        return "bg-gray-500/10 border-gray-500/30 text-gray-700 dark:text-gray-400";
    }
  };

  // Group products by type affiché (consommable si product_type ou product_usage internal_consumption)
  const groupedProducts = useMemo(() => {
    const groups: Record<string, Product[]> = {};
    filteredByTag.forEach((product) => {
      const effectiveType = getEffectiveDisplayType(product) as Parameters<typeof getProductFamilyLabel>[0];
      const family = getProductFamilyLabel(effectiveType);
      if (!groups[family]) {
        groups[family] = [];
      }
      groups[family].push(product);
    });
    return groups;
  }, [filteredByTag]);

  // Convert products to entity list items: toujours 4 catégories (Produit, Service, Licence, Consommable)
  const entityListItems: EntityListItem[] = useMemo(() => {
    const items: EntityListItem[] = [];

    SIDEBAR_TYPE_ORDER.forEach((productType) => {
      const family = getProductFamilyLabel(productType);
      const products = groupedProducts[family] ?? [];

      items.push({
        id: `group-${family}`,
        title: family.toUpperCase(),
        subtitle: `${products.length} produit(s)`,
        icon: <Package size={16} strokeWidth={1.5} />,
        groupHeader: true,
        groupColor: getProductFamilyColor(productType),
      });

      products
        .sort((a, b) => a.name.localeCompare(b.name))
        .forEach((product) => {
          const isConsommable = getEffectiveDisplayType(product) === "consommable";
          const tagsStr = (product.tags || []).filter(Boolean).join(", ");
          const opsFamilyStr = (product as { ops_family?: string | null }).ops_family || "";
          const infoStr = isConsommable && (tagsStr || opsFamilyStr)
            ? [tagsStr, opsFamilyStr].filter(Boolean).join(" • ")
            : undefined;
          items.push({
            id: product.id,
            title: product.name,
            subtitle: product.code || undefined,
            info: infoStr,
            icon: <Package size={16} strokeWidth={1.5} />,
            status: {
              label: product.status === "active" ? "Actif" : product.status === "archived" ? "Archivé" : "Inactif",
              tone: product.status === "active" ? "success" : product.status === "archived" ? "neutral" : "warning",
            },
          });
        });
    });

    return items;
  }, [groupedProducts]);

  const handleCreate = async () => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom est requis";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    try {
      const unitToSend = normalizeUnitForType(form.product_type, form.unit_kind || null, form.howBilling);
      const createPayload: Record<string, unknown> = {
        code: form.code || null,
        name: form.name,
        product_type: form.product_type,
        product_usage: form.product_usage ?? "sold",
        status: form.status,
        description_md: form.description_md || null,
        unit_kind: unitToSend,
        tags: form.tags.length > 0 ? form.tags : null,
        track_inventory: false,
        default_supplier_id: null,
        internal_fixed_cost: null,
        tax_code: form.tax_code,
        tax_profile: form.tax_profile as "qc_standard" | "qc_zero" | "custom",
        minimum_charge: form.minimum_charge ?? undefined,
      };
      if (form.product_type === "bundle") {
        createPayload.operational_cost_profile = {
          bundle_base_price: form.bundle_base_price ?? undefined,
          bundle_items: [],
        };
        createPayload.bundle_family = form.bundle_family || null;
      }
      const created = await createProduct(createPayload);

      const billingOpt = BILLING_OPTIONS.find((o) => o.value === form.howBilling);
      if (billingOpt && form.product_usage === "sold" && form.product_type !== "bundle") {
        await createProductPricingModel(created.id, {
          mode: billingOpt.mode,
          base_rate: 0,
          min_price: null,
          params: billingOpt.mode === "per_unit" ? { unit: unitToSend || "each" } : null,
        });
      }

      if (form.category_ids.length > 0) {
        await putProductCategories(created.id, form.category_ids);
      }

      const all = await loadAllProducts();
      setProducts(all);
      setSelectedId(created.id);
      setOpen(false);
      resetForm();
    } catch (error) {
      console.error("Failed to create product:", error);
      alert(`Erreur lors de la création du produit/service: ${(error as Error).message || "Veuillez réessayer."}`);
    }
  };

  const handleUpdate = async () => {
    if (!editProduct) return;
    if (updateSaving) return;

    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) {
      newErrors.name = "Le nom est requis";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setUpdateSaving(true);
    const billingOpt = BILLING_OPTIONS.find((o) => o.value === form.howBilling);
    const effectiveUnitKind = normalizeUnitForType(form.product_type, form.unit_kind || null, form.howBilling);
    const updatePayload: Record<string, unknown> = {
      code: form.code || null,
      name: form.name,
      product_type: form.product_type,
      product_usage: form.product_usage ?? "sold",
      status: form.status,
      description_md: form.description_md || null,
      unit_kind: effectiveUnitKind,
      tags: form.tags.length > 0 ? form.tags : null,
      ops_family: form.ops_family?.trim() || null,
      tax_code: form.tax_code,
      document_detail_template_md: form.document_detail_template_md || null,
      service_metadata: form.service_metadata || null,
      minimum_charge: form.minimum_charge ?? undefined,
    };
    if (form.product_type === "bundle") {
      updatePayload.track_inventory = false;
      const existingProfile = (editProduct as Product & { operational_cost_profile?: Record<string, unknown> }).operational_cost_profile ?? {};
      updatePayload.operational_cost_profile = {
        ...existingProfile,
        bundle_base_price: form.bundle_base_price ?? undefined,
        // preserve bundle_items from existing profile (configured in Général tab)
        bundle_items: Array.isArray(existingProfile.bundle_items) ? existingProfile.bundle_items : [],
      };
      updatePayload.bundle_family = form.bundle_family || null;
    }
    try {
      await updateProduct(editProduct.id, updatePayload);
      if (billingOpt && form.product_usage === "sold" && form.product_type !== "bundle") {
        await createProductPricingModel(editProduct.id, {
          mode: billingOpt.mode,
          base_rate: 0,
          min_price: null,
          params: billingOpt.mode === "per_unit" ? { unit: effectiveUnitKind || "each" } : null,
        });
      }
      await putProductCategories(editProduct.id, form.category_ids);
      const all = await loadAllProducts();
      setProducts(all);
      setEditProduct(null);
      resetForm();
    } catch (error) {
      console.error("Failed to update product:", error);
      alert(`Erreur lors de la mise à jour du produit/service: ${(error as Error).message || "Veuillez réessayer."}`);
    } finally {
      setUpdateSaving(false);
    }
  };

  const handleArchive = async (productId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir archiver ce produit/service ?")) return;
    try {
      await updateProduct(productId, { status: "archived" });
      const all = await loadAllProducts();
      setProducts(all);
    } catch (error) {
      console.error("Failed to archive product:", error);
      alert("Erreur lors de l'archivage. Veuillez réessayer.");
    }
  };

  const handleDelete = async (productId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer définitivement ce produit/service ? S'il est utilisé dans une facture, il sera archivé à la place.")) return;
    try {
      const result = await deleteProduct(productId);
      const all = await loadAllProducts();
      setProducts(all);
      setSelectedId(null);
      if ("archived" in result && result.archived) {
        alert(result.message);
      } else {
        alert("Produit supprimé définitivement.");
      }
    } catch (error) {
      console.error("Failed to delete product:", error);
      alert("Erreur lors de la suppression. Veuillez réessayer.");
    }
  };

  const handleDuplicateBundle = async (product: Product) => {
    if (product.product_type !== "bundle") return;
    setDuplicateLoading(true);
    try {
      const full = await getProduct(product.id) as Product & { operational_cost_profile?: { bundle_base_price?: unknown; bundle_items?: unknown[] } };
      const profile = full.operational_cost_profile ?? {};
      const createPayload: Record<string, unknown> = {
        code: null,
        name: `Copie de ${full.name}`,
        product_type: "bundle",
        product_usage: full.product_usage ?? "sold",
        status: "active",
        description_md: full.description_md || null,
        unit_kind: full.unit_kind || null,
        tags: full.tags?.length ? full.tags : null,
        track_inventory: false,
        default_supplier_id: null,
        internal_fixed_cost: null,
        tax_code: full.tax_code || "STANDARD_QC",
        tax_profile: full.tax_profile || "qc_standard",
        minimum_charge: (full as Product & { minimum_charge?: number | null }).minimum_charge ?? undefined,
        bundle_family: (full as Product & { bundle_family?: string | null }).bundle_family ?? null,
        operational_cost_profile: {
          bundle_base_price: profile.bundle_base_price ?? undefined,
          bundle_items: Array.isArray(profile.bundle_items) ? profile.bundle_items : [],
        },
      };
      const created = await createProduct(createPayload);
      const all = await loadAllProducts();
      setProducts(all);
      setSelectedId(created.id);
    } catch (error) {
      console.error("Failed to duplicate bundle:", error);
      alert(`Erreur lors de la duplication : ${(error as Error).message ?? "Veuillez réessayer."}`);
    } finally {
      setDuplicateLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      code: "",
      name: "",
      product_type: "produit",
      product_usage: "sold",
      status: "active",
      description_md: "",
      unit_kind: "each",
      tags: [],
      howBilling: "fixed",
      tax_code: "STANDARD_QC",
      tax_profile: "qc_standard",
      document_detail_template_md: "",
      service_metadata: null,
      category_ids: [],
      minimum_charge: null,
      bundle_base_price: null,
      bundle_family: "",
      ops_family: "",
    });
    setErrors({});
  };

  const handleCancel = () => {
    resetForm();
    setOpen(false);
    setEditProduct(null);
  };

  const modeToBillingChoice = (mode: string): BillingChoice => {
    if (mode === "fixed") return "fixed";
    if (mode === "per_unit") return "per_unit";
    return "fixed";
  };

  const handleEdit = async (product: Product) => {
    setEditButtonLoading(true);
    try {
      const full = await getProduct(product.id);
      setEditProduct(full);
      let howBilling: BillingChoice = "fixed";
      let categoryIds: string[] = [];
      try {
        const pm = await getProductPricingModel(full.id);
        if (pm) howBilling = modeToBillingChoice(pm.mode);
      } catch {
        // keep default fixed
      }
      const defaultForType = getDefaultBillingForType(product.product_type);
      const allowedForType = getBillingOptionsForType(product.product_type);
      if (!allowedForType.some((b) => b.value === howBilling)) howBilling = defaultForType;
      const unitKind = (full.unit_kind as string) || "each";
      try {
        const cats = await getProductCategories(full.id);
        categoryIds = cats.map((c) => c.id);
      } catch {
        // ignore
      }
      const profile = (full as Product & { operational_cost_profile?: { bundle_base_price?: BundleBasePrice | null } }).operational_cost_profile;
      setForm({
        code: full.code || "",
        name: full.name,
        product_type: full.product_type,
        product_usage: full.product_usage ?? "sold",
        status: full.status,
        description_md: full.description_md || "",
        unit_kind: unitKind,
        tags: full.tags || [],
        howBilling,
        tax_code: full.tax_code || "STANDARD_QC",
        tax_profile: full.tax_profile || "qc_standard",
        document_detail_template_md: "",
        service_metadata: null,
        category_ids: categoryIds,
        minimum_charge: (full as Product & { minimum_charge?: number | null }).minimum_charge ?? null,
        bundle_base_price: profile?.bundle_base_price ?? null,
        bundle_family: (full as Product & { bundle_family?: string | null }).bundle_family ?? "",
        ops_family: (full as Product & { ops_family?: string | null }).ops_family ?? "",
      });
      setEditProduct(full);
    } catch (err) {
      console.error("Failed to open edit modal:", err);
      alert(`Impossible d'ouvrir l'édition : ${(err as Error).message ?? "Veuillez réessayer."}`);
    } finally {
      setEditButtonLoading(false);
    }
  };

  const config: EntityWorkspaceBoardConfig = useMemo(
    () => ({
      entityType: "client", // Reuse client type for now
      topbarTitle: "Catalogue",
      listTitle: "Services & Produits",
      listIcon: <Package size={16} strokeWidth={1.5} />,
      storageKey: "xlflow.catalog.list.sidebar",
      allowHoverOpen: true,
      defaultCollapsedGroups: true,
      boardSectionId: "overview",
      sections: [],
      entityListItems,
      entities: [],
      searchPlaceholder: "Rechercher par nom ou code…",
    }),
    [entityListItems]
  );

  // Get available product types for current family
  const availableProductTypes = useMemo(() => {
    if (activeFamily === "all") return PRODUCT_TYPE_OPTIONS;
    return PRODUCT_TYPE_OPTIONS.filter((opt) => opt.family === activeFamily);
  }, [activeFamily]);

  return (
    <>
      <div className="flex flex-col h-screen overflow-hidden">
        {/* Header with tabs and filters */}
        <div className="flex-shrink-0 border-b border-border bg-surface p-4 space-y-4">
          {/* Family tabs */}
          <Tabs
            tabs={PRODUCT_FAMILIES.map((f) => ({ id: f.id, label: f.label }))}
            activeId={activeFamily}
            onChange={(id) => {
              setActiveFamily(id as ProductFamily);
              setSelectedId(null);
            }}
            variant="underline"
          />
          {/* Filtre par tag (surtout utile pour les consommables) */}
          {distinctTags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Select
                label="Filtrer par tag"
                hideLabel
                options={["Tous", ...distinctTags]}
                value={selectedTagFilter ?? "Tous"}
                onChange={(value) => setSelectedTagFilter(value === "Tous" ? null : value)}
              />
            </div>
          )}
        </div>

        {/* Main content : prend le reste de l'écran, pas de scroll page */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <GenericEntityWorkspacePage
            config={config}
            userName={userName}
            containerFluid
            items={entityListItems}
            selectedId={selectedId}
            onSelect={setSelectedId}
            addActionLabel="+ Ajouter"
            onAddAction={() => {
              resetForm();
              setEditProduct(null);
              setOpen(true);
            }}
            customContent={
              selectedProduct ? (
                <div className="space-y-4">
                  {/* Header */}
                  <div className="border-b border-border bg-surface p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-text">{selectedProduct.name}</h2>
                        {selectedProduct.code && (
                          <p className="text-sm text-muted">Code: {selectedProduct.code}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <IconButton
                          icon={<Pencil size={16} strokeWidth={1.5} />}
                          ariaLabel="Modifier"
                          intent="ghost"
                          size="sm"
                          state={editButtonLoading ? "loading" : "default"}
                          onClick={() => handleEdit(selectedProduct)}
                        />
                        {selectedProduct.product_type === "bundle" && (
                          <IconButton
                            icon={<Copy size={16} strokeWidth={1.5} />}
                            ariaLabel="Dupliquer le bundle"
                            intent="ghost"
                            size="sm"
                            state={duplicateLoading ? "loading" : "default"}
                            onClick={() => handleDuplicateBundle(selectedProduct)}
                          />
                        )}
                        {selectedProduct.status !== "archived" && (
                          <IconButton
                            icon={<Archive size={16} strokeWidth={1.5} />}
                            ariaLabel="Archiver"
                            intent="ghost"
                            size="sm"
                            onClick={() => handleArchive(selectedProduct.id)}
                          />
                        )}
                        <IconButton
                          icon={<Trash2 size={16} strokeWidth={1.5} />}
                          ariaLabel="Supprimer"
                          intent="ghost"
                          size="sm"
                          onClick={() => handleDelete(selectedProduct.id)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Tabs — Approvisionnement masqué pour service et data_processing */}
                  <div className="px-4">
                    <Tabs
                      tabs={[
                        { id: "general", label: "Général" },
                        { id: "pricing", label: "Pricing" },
                        { id: "deliverables", label: "Livrables" },
                        { id: "process", label: "Orchestration" },
                        { id: "internal_costs", label: "Coûts internes" },
                        ...(selectedProduct.product_type !== "service" &&
                        selectedProduct.product_type !== "data_processing"
                          ? [{ id: "inventory", label: "Approvisionnement" }]
                          : []),
                        { id: "sales", label: "Ventes" },
                        { id: "categories", label: "Catégories" },
                        { id: "components", label: "Composants (BOM)" },
                      ]}
                      activeId={activeTab}
                      onChange={(id) => setActiveTab(id as typeof activeTab)}
                      variant="underline"
                    />
                  </div>

                  {/* Tab Content */}
                  {activeTab === "general" && (
                    <ProductGeneralTab
                      productId={selectedProduct.id}
                      onProductUpdate={(updated) => {
                        const updatedProducts = products.map((p) =>
                          p.id === updated.id ? updated : p
                        );
                        setProducts(updatedProducts);
                      }}
                      suggestedTagValues={distinctTags}
                      suggestedOpsFamilyValues={distinctOpsFamilies}
                    />
                  )}
                  {activeTab === "pricing" && (
                    <ProductPricingTab productId={selectedProduct.id} />
                  )}
                  {activeTab === "deliverables" && (
                    <ProductDeliverablesTab productId={selectedProduct.id} />
                  )}
                  {activeTab === "process" && (
                    <ProductOrchestrationTab productId={selectedProduct.id} />
                  )}
                  {activeTab === "internal_costs" && (
                    <ProductInternalCostsTab
                      productId={selectedProduct.id}
                      onProductUpdate={(updated) => {
                        const updatedProducts = products.map((p) =>
                          p.id === updated.id ? updated : p
                        );
                        setProducts(updatedProducts);
                      }}
                    />
                  )}
                  {activeTab === "inventory" && (
                    <ProductInventoryTab
                      productId={selectedProduct.id}
                      onNavigateToGeneral={() => setActiveTab("general")}
                    />
                  )}
                  {activeTab === "sales" && (
                    <ProductSalesTab productId={selectedProduct.id} />
                  )}
                  {activeTab === "categories" && (
                    <ProductCategoriesTab productId={selectedProduct.id} />
                  )}
                  {activeTab === "components" && (
                    <ProductComponentsTab productId={selectedProduct.id} />
                  )}
                </div>
              ) : (
                <div className="p-6">
                  <p className="text-sm text-muted">Sélectionnez un produit/service pour voir les détails</p>
                </div>
              )
            }
          />
        </div>
      </div>

      {/* Create: modal only. Edit: right-side drawer (keeps list + tabs context visible). */}
      <Modal
        title={form.product_type === "bundle" ? "Créer un bundle" : "Créer un produit/service"}
        open={open && !editProduct}
        onClose={handleCancel}
        showDefaultButtons={false}
        showCloseButton={true}
        size="xl"
      >
        <div className="space-y-3">
          <div>
            <Input
              label="Nom"
              placeholder="Nom du produit/service"
              value={form.name}
              onChange={(event) => {
                setForm({ ...form, name: event.target.value });
                if (errors.name) setErrors({ ...errors, name: "" });
              }}
              state={errors.name ? "error" : "default"}
            />
            {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Input
              label="Code (optionnel)"
              placeholder="SKU ou code produit"
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
            />
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-sm text-muted">Type</label>
                <div className="group relative">
                  <HelpCircle size={14} className="text-primary cursor-help hover:text-primary/80 transition-colors" aria-hidden />
                  <div className="absolute left-0 top-full mt-2 w-80 max-h-[300px] overflow-y-auto p-4 rounded-lg border border-border bg-surface shadow-lg text-xs text-text opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <p className="text-muted leading-relaxed">{TYPE_HELP}</p>
                  </div>
                </div>
              </div>
              <Select
                options={MODAL_TYPE_OPTIONS.map((opt) => opt.label)}
                value={MODAL_TYPE_OPTIONS.find((opt) => opt.value === form.product_type)?.label || MODAL_TYPE_OPTIONS[0].label}
                onChange={(value) => {
                  const option = MODAL_TYPE_OPTIONS.find((opt) => opt.label === value);
                  if (!option) return;
                  const newType = option.value;
                  const allowedUnits = UNIT_OPTIONS_BY_TYPE[newType] ?? UNIT_OPTIONS_BY_TYPE.hardware;
                  const allowedBilling = getBillingOptionsForType(newType);
                  const defaultBilling = getDefaultBillingForType(newType);
                  let unit_kind = form.unit_kind;
                  let howBilling = form.howBilling;
                  if (!allowedBilling.some((b) => b.value === howBilling)) {
                    howBilling = defaultBilling;
                    unit_kind = allowedBilling.find((b) => b.value === howBilling)?.defaultUnitKind ?? "each";
                  }
                  if (!allowedUnits.some((u) => u.value === unit_kind)) unit_kind = allowedUnits[0]?.value ?? "each";
                  setForm({
                    ...form,
                    product_type: newType,
                    unit_kind,
                    howBilling,
                  });
                }}
              />
            </div>
            {form.product_type === "bundle" && (
              <div className="md:col-span-2 space-y-3">
                <div>
                  <label className="text-sm font-medium text-text mb-2 block">Famille de bundle</label>
                  <Select
                    options={BUNDLE_FAMILY_OPTIONS.map((o) => o.label)}
                    value={BUNDLE_FAMILY_OPTIONS.find((o) => o.value === (form.bundle_family || ""))?.label ?? "Sans famille"}
                    onChange={(value) => {
                      const opt = BUNDLE_FAMILY_OPTIONS.find((o) => o.label === value);
                      setForm({ ...form, bundle_family: opt?.value ?? "" });
                    }}
                  />
                  <p className="text-xs text-muted mt-1">Typologie métier pour le filtrage et la présentation dans la soumission.</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="bundle-has-base"
                    checked={form.bundle_base_price != null}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        bundle_base_price: e.target.checked
                          ? { label: "Frais de dossier", mode: "fixed", value: 0, unit: "each" }
                          : null,
                      })
                    }
                  />
                  <label htmlFor="bundle-has-base" className="text-sm font-medium text-text">Ce bundle a un prix de base (ex. frais de dossier)</label>
                </div>
                {form.bundle_base_price != null && (
                  <div className="pl-6 space-y-2 border-l-2 border-border">
                    <Input
                      label="Libellé du prix de base"
                      placeholder="ex. Frais de dossier"
                      value={form.bundle_base_price.label}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          bundle_base_price: form.bundle_base_price ? { ...form.bundle_base_price, label: e.target.value } : null,
                        })
                      }
                    />
                    <div>
                      <label className="text-sm font-medium text-text mb-1 block">Mode</label>
                      <Select
                        options={["Prix fixe", "Par unité"]}
                        value={form.bundle_base_price.mode === "fixed" ? "Prix fixe" : "Par unité"}
                        onChange={(value) =>
                          setForm({
                            ...form,
                            bundle_base_price: form.bundle_base_price
                              ? { ...form.bundle_base_price, mode: value === "Par unité" ? "per_unit" : "fixed", unit: value === "Par unité" ? "each" : undefined }
                              : null,
                          })
                        }
                      />
                    </div>
                    <Input
                      label={form.bundle_base_price.mode === "fixed" ? "Montant (CAD)" : "Montant par unité (CAD)"}
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.bundle_base_price.value === 0 ? "" : String(form.bundle_base_price.value)}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setForm({
                          ...form,
                          bundle_base_price: form.bundle_base_price ? { ...form.bundle_base_price, value: isNaN(v) ? 0 : v } : null,
                        });
                      }}
                    />
                    {form.bundle_base_price.mode === "per_unit" && (
                      <div>
                        <label className="text-sm font-medium text-text mb-1 block">Unité</label>
                        <Select
                          options={BUNDLE_BASE_UNITS.map((o) => o.label)}
                          value={BUNDLE_BASE_UNITS.find((o) => o.value === (form.bundle_base_price?.unit ?? "each"))?.label ?? "Chaque"}
                          onChange={(value) => {
                            const opt = BUNDLE_BASE_UNITS.find((o) => o.label === value);
                            if (opt && form.bundle_base_price)
                              setForm({ ...form, bundle_base_price: { ...form.bundle_base_price, unit: opt.value } });
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted">
                  Contenu obligatoire et optionnel : à configurer dans l'onglet Général après création.
                </p>
              </div>
            )}
            <div>
            <div>
              <label className="text-sm font-medium text-text mb-2 block">Ce produit est-il vendu ?</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
                  <input
                    type="radio"
                    name="product_usage"
                    checked={form.product_usage === "sold"}
                    onChange={() => setForm({ ...form, product_usage: "sold" })}
                    className="h-4 w-4"
                  />
                  Oui
                </label>
                <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
                  <input
                    type="radio"
                    name="product_usage"
                    checked={form.product_usage === "internal_consumption"}
                    onChange={() => setForm({ ...form, product_usage: "internal_consumption" })}
                    className="h-4 w-4"
                  />
                  Non, consommé en interne
                </label>
              </div>
              <p className="text-xs text-muted mt-1">Si non, pas de tarification ni de prix de vente.</p>
            </div>
          </div>
          </div>
          <div>
            <label className="text-sm text-muted mb-2 block">Description</label>
            <textarea
              className="w-full h-24 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
              placeholder="Description du produit/service..."
              value={form.description_md}
              onChange={(event) => setForm({ ...form, description_md: event.target.value })}
            />
          </div>
          {/* Section 2 - Facturation (uniquement si vendu) */}
          {form.product_usage === "sold" && (
          <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-text border-b border-border pb-2">Facturation</h3>
            {form.product_type === "bundle" && (
              <p className="text-sm text-muted">
                Le total du bundle = prix de base (si configuré) + somme des items obligatoires + somme des options choisies. Chaque item garde son mode de prix (fixe ou par unité).
              </p>
            )}
            {form.product_type !== "bundle" ? (
            <>
            <div>
              <label className="text-sm font-medium text-text mb-2 block">Mode de tarification *</label>
              <Select
                options={getBillingOptionsForType(form.product_type).map((o) => o.label)}
                value={getBillingOptionsForType(form.product_type).find((o) => o.value === form.howBilling)?.label ?? getBillingOptionsForType(form.product_type)[0]?.label ?? ""}
                onChange={(value) => {
                  const allowedBilling = getBillingOptionsForType(form.product_type);
                  const opt = allowedBilling.find((o) => o.label === value);
                  if (opt) {
                    const allowedUnits = UNIT_OPTIONS_BY_TYPE[form.product_type] ?? UNIT_OPTIONS_BY_TYPE.produit;
                    let unit = opt.defaultUnitKind;
                    if (!allowedUnits.some((u) => u.value === unit)) unit = allowedUnits[0]?.value ?? "each";
                    setForm({ ...form, howBilling: opt.value, unit_kind: unit });
                  }
                }}
              />
              {getBillingOptionsForType(form.product_type).find((o) => o.value === form.howBilling)?.description && (
                <p className="text-xs text-muted mt-1">
                  {getBillingOptionsForType(form.product_type).find((o) => o.value === form.howBilling)?.description}
                </p>
              )}
            </div>
            {form.howBilling === "fixed" ? (
              <div className="text-sm text-muted">Unité : <strong className="text-text">Chaque</strong> (prix fixe par unité).</div>
            ) : form.howBilling === "per_unit" ? (
              (() => {
                const unitOptions = UNIT_OPTIONS_BY_TYPE[form.product_type] ?? UNIT_OPTIONS_BY_TYPE.produit;
                const current = unitOptions.find((o) => o.value === form.unit_kind) ?? unitOptions[0];
                return (
                  <div>
                    <label className="text-sm font-medium text-muted mb-2 block">Type de quantité</label>
                    <Select
                      options={unitOptions.map((o) => o.label)}
                      value={current?.label ?? ""}
                      onChange={(value) => {
                        const opt = unitOptions.find((o) => o.label === value);
                        if (opt) setForm({ ...form, unit_kind: opt.value });
                      }}
                    />
                  </div>
                );
              })()
            ) : null}
            </>
            ) : null}
            <Select
              label="Taxe"
              options={TAX_CODE_OPTIONS.map((o) => o.label)}
              value={TAX_CODE_OPTIONS.find((o) => o.value === form.tax_code)?.label || ""}
              onChange={(value) => {
                const opt = TAX_CODE_OPTIONS.find((o) => o.label === value);
                if (opt) {
                  setForm({
                    ...form,
                    tax_code: opt.value,
                    tax_profile: opt.value === "STANDARD_QC" ? "qc_standard" : "qc_zero",
                  });
                }
              }}
            />
            {form.product_type === "data_processing" && (
              <div>
                <Input
                  label="Minimum de facturation (CAD)"
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="ex. 150"
                  value={form.minimum_charge != null ? String(form.minimum_charge) : ""}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    setForm({ ...form, minimum_charge: v === "" ? null : parseFloat(v) || null });
                  }}
                />
                <p className="text-xs text-muted mt-1">Facturation = max(quantité × prix unitaire, minimum). Laissez vide si aucun minimum.</p>
              </div>
            )}
          </div>
          )}
          {/* Section 3 - Classification (produit only) */}
          {form.product_type === "produit" && (
            <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-3">
              <div className="flex items-center gap-2 border-b border-border pb-2">
                <h3 className="text-sm font-semibold text-text">Classification</h3>
                <div className="group relative">
                  <HelpCircle size={14} className="text-primary cursor-help hover:text-primary/80 transition-colors" aria-hidden />
                  <div className="absolute left-0 top-full mt-2 w-80 max-h-[200px] overflow-y-auto p-4 rounded-lg border border-border bg-surface shadow-lg text-xs text-muted opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <p className="leading-relaxed">{CATEGORY_HELP}</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm text-muted mb-2 block">Catégories</label>
                <div className="flex flex-wrap gap-2 min-h-[40px] rounded-md border border-border bg-surface px-3 py-2">
                  {categoriesList.map((cat) => {
                    const selected = form.category_ids.includes(cat.id);
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => {
                          setForm({
                            ...form,
                            category_ids: selected
                              ? form.category_ids.filter((id) => id !== cat.id)
                              : [...form.category_ids, cat.id],
                          });
                        }}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected ? "bg-primary/15 border-primary text-primary" : "border-border bg-surface-muted/50 text-muted hover:border-primary/50"}`}
                      >
                        {cat.name}
                      </button>
                    );
                  })}
                  {categoriesList.length === 0 && <span className="text-muted text-sm">Chargement…</span>}
                </div>
              </div>
            </div>
          )}
          {/* Section 5 - Opérationnel */}
          <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-text border-b border-border pb-2">Opérationnel</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <Select
              label="Statut"
              options={STATUS_OPTIONS.map((opt) => opt.label)}
              value={STATUS_OPTIONS.find((opt) => opt.value === form.status)?.label || ""}
              onChange={(value) => {
                const option = STATUS_OPTIONS.find((opt) => opt.label === value);
                if (option) setForm({ ...form, status: option.value });
              }}
            />
          </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button label="Annuler" intent="default" size="sm" onClick={handleCancel} />
            <Button
              label="Créer"
              intent="primary"
              size="sm"
              onClick={handleCreate}
            />
          </div>
        </div>
      </Modal>

      {/* Edit product: right panel (drawer) — complements workspace without replacing it */}
      {editProduct && (
        <Drawer
          title={editProduct.product_type === "bundle" ? "Modifier le bundle" : "Modifier le produit/service"}
          open={true}
          onClose={() => {
            resetForm();
            setEditProduct(null);
          }}
          size="2xl"
          footer={
            <div className="flex justify-end gap-2">
              <Button
                label="Annuler"
                intent="default"
                size="sm"
                onClick={() => {
                  resetForm();
                  setEditProduct(null);
                }}
              />
              <Button
                label={updateSaving ? "Enregistrement…" : "Enregistrer"}
                intent="primary"
                size="sm"
                onClick={handleUpdate}
                disabled={updateSaving}
              />
            </div>
          }
        >
          <div className="space-y-3">
            <div>
              <Input
                label="Nom"
                placeholder="Nom du produit/service"
                value={form.name}
                onChange={(event) => {
                  setForm({ ...form, name: event.target.value });
                  if (errors.name) setErrors({ ...errors, name: "" });
                }}
                state={errors.name ? "error" : "default"}
              />
              {errors.name && <p className="mt-1 text-xs text-danger">{errors.name}</p>}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <Input
                label="Code (optionnel)"
                placeholder="SKU ou code produit"
                value={form.code}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
              />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm text-muted">Type</label>
                  <div className="group relative">
                    <HelpCircle size={14} className="text-primary cursor-help hover:text-primary/80 transition-colors" aria-hidden />
                    <div className="absolute left-0 top-full mt-2 w-80 max-h-[300px] overflow-y-auto p-4 rounded-lg border border-border bg-surface shadow-lg text-xs text-text opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <p className="text-muted leading-relaxed">{TYPE_HELP}</p>
                    </div>
                  </div>
                </div>
                <Select
                  options={MODAL_TYPE_OPTIONS.map((opt) => opt.label)}
                  value={MODAL_TYPE_OPTIONS.find((opt) => opt.value === form.product_type)?.label || MODAL_TYPE_OPTIONS[0].label}
                  onChange={(value) => {
                    const option = MODAL_TYPE_OPTIONS.find((opt) => opt.label === value);
                    if (!option) return;
                    const newType = option.value;
                    const allowedUnits = UNIT_OPTIONS_BY_TYPE[newType] ?? UNIT_OPTIONS_BY_TYPE.produit;
                    const allowedBilling = getBillingOptionsForType(newType);
                    const defaultBilling = getDefaultBillingForType(newType);
                    let unit_kind = form.unit_kind;
                    let howBilling = form.howBilling;
                    if (!allowedBilling.some((b) => b.value === howBilling)) {
                      howBilling = defaultBilling;
                      unit_kind = allowedBilling.find((b) => b.value === howBilling)?.defaultUnitKind ?? "each";
                    }
                    if (!allowedUnits.some((u) => u.value === unit_kind)) unit_kind = allowedUnits[0]?.value ?? "each";
                    setForm({
                      ...form,
                      product_type: newType,
                      unit_kind,
                      howBilling,
                    });
                  }}
                />
              </div>
            </div>
            {form.product_type === "bundle" && (
              <div className="md:col-span-2 space-y-3">
                <div>
                  <label className="text-sm font-medium text-text mb-2 block">Famille de bundle</label>
                  <Select
                    options={BUNDLE_FAMILY_OPTIONS.map((o) => o.label)}
                    value={BUNDLE_FAMILY_OPTIONS.find((o) => o.value === (form.bundle_family || ""))?.label ?? "Sans famille"}
                    onChange={(value) => {
                      const opt = BUNDLE_FAMILY_OPTIONS.find((o) => o.label === value);
                      setForm({ ...form, bundle_family: opt?.value ?? "" });
                    }}
                  />
                  <p className="text-xs text-muted mt-1">Typologie métier pour le filtrage et la présentation dans la soumission.</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="bundle-has-base-drawer"
                    checked={form.bundle_base_price != null}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        bundle_base_price: e.target.checked
                          ? { label: "Frais de dossier", mode: "fixed", value: 0, unit: "each" }
                          : null,
                      })
                    }
                  />
                  <label htmlFor="bundle-has-base-drawer" className="text-sm font-medium text-text">Ce bundle a un prix de base (ex. frais de dossier)</label>
                </div>
                {form.bundle_base_price != null && (
                  <div className="pl-6 space-y-2 border-l-2 border-border">
                    <Input
                      label="Libellé du prix de base"
                      placeholder="ex. Frais de dossier"
                      value={form.bundle_base_price.label}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          bundle_base_price: form.bundle_base_price ? { ...form.bundle_base_price, label: e.target.value } : null,
                        })
                      }
                    />
                    <div>
                      <label className="text-sm font-medium text-text mb-1 block">Mode</label>
                      <Select
                        options={["Prix fixe", "Par unité"]}
                        value={form.bundle_base_price.mode === "fixed" ? "Prix fixe" : "Par unité"}
                        onChange={(value) =>
                          setForm({
                            ...form,
                            bundle_base_price: form.bundle_base_price
                              ? { ...form.bundle_base_price, mode: value === "Par unité" ? "per_unit" : "fixed", unit: value === "Par unité" ? "each" : undefined }
                              : null,
                          })
                        }
                      />
                    </div>
                    <Input
                      label={form.bundle_base_price.mode === "fixed" ? "Montant (CAD)" : "Montant par unité (CAD)"}
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.bundle_base_price.value === 0 ? "" : String(form.bundle_base_price.value)}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        setForm({
                          ...form,
                          bundle_base_price: form.bundle_base_price ? { ...form.bundle_base_price, value: isNaN(v) ? 0 : v } : null,
                        });
                      }}
                    />
                    {form.bundle_base_price.mode === "per_unit" && (
                      <div>
                        <label className="text-sm font-medium text-text mb-1 block">Unité</label>
                        <Select
                          options={BUNDLE_BASE_UNITS.map((o) => o.label)}
                          value={BUNDLE_BASE_UNITS.find((o) => o.value === (form.bundle_base_price?.unit ?? "each"))?.label ?? "Chaque"}
                          onChange={(value) => {
                            const opt = BUNDLE_BASE_UNITS.find((o) => o.label === value);
                            if (opt && form.bundle_base_price)
                              setForm({ ...form, bundle_base_price: { ...form.bundle_base_price, unit: opt.value } });
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                <p className="text-xs text-muted">
                  Contenu obligatoire et optionnel : à configurer dans l'onglet Général après création.
                </p>
              </div>
            )}
            <div>
              <div>
                <label className="text-sm font-medium text-text mb-2 block">Ce produit est-il vendu ?</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
                    <input
                      type="radio"
                      name="product_usage_drawer"
                      checked={form.product_usage === "sold"}
                      onChange={() => setForm({ ...form, product_usage: "sold" })}
                      className="h-4 w-4"
                    />
                    Oui
                  </label>
                  <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
                    <input
                      type="radio"
                      name="product_usage_drawer"
                      checked={form.product_usage === "internal_consumption"}
                      onChange={() => setForm({ ...form, product_usage: "internal_consumption" })}
                      className="h-4 w-4"
                    />
                    Non, consommé en interne
                  </label>
                </div>
                <p className="text-xs text-muted mt-1">Si non, pas de tarification ni de prix de vente.</p>
              </div>
            </div>
            <div>
              <label className="text-sm text-muted mb-2 block">Description</label>
              <textarea
                className="w-full h-24 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text"
                placeholder="Description du produit/service..."
                value={form.description_md}
                onChange={(event) => setForm({ ...form, description_md: event.target.value })}
              />
            </div>
            {form.product_usage === "sold" && (
              <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-3">
                <h3 className="text-sm font-semibold text-text border-b border-border pb-2">Facturation</h3>
                {form.product_type !== "bundle" ? (
                  <>
                    <div>
                      <label className="text-sm font-medium text-text mb-2 block">Mode de tarification *</label>
                      <Select
                        options={getBillingOptionsForType(form.product_type).map((o) => o.label)}
                        value={getBillingOptionsForType(form.product_type).find((o) => o.value === form.howBilling)?.label ?? getBillingOptionsForType(form.product_type)[0]?.label ?? ""}
                        onChange={(value) => {
                          const allowedBilling = getBillingOptionsForType(form.product_type);
                          const opt = allowedBilling.find((o) => o.label === value);
                          if (opt) {
                            const allowedUnits = UNIT_OPTIONS_BY_TYPE[form.product_type] ?? UNIT_OPTIONS_BY_TYPE.produit;
                            let unit = opt.defaultUnitKind;
                            if (!allowedUnits.some((u) => u.value === unit)) unit = allowedUnits[0]?.value ?? "each";
                            setForm({ ...form, howBilling: opt.value, unit_kind: unit });
                          }
                        }}
                      />
                    </div>
                    {form.howBilling === "per_unit" && (
                      (() => {
                        const unitOptions = UNIT_OPTIONS_BY_TYPE[form.product_type] ?? UNIT_OPTIONS_BY_TYPE.produit;
                        const current = unitOptions.find((o) => o.value === form.unit_kind) ?? unitOptions[0];
                        return (
                          <div>
                            <label className="text-sm font-medium text-muted mb-2 block">Type de quantité</label>
                            <Select
                              options={unitOptions.map((o) => o.label)}
                              value={current?.label ?? ""}
                              onChange={(value) => {
                                const opt = unitOptions.find((o) => o.label === value);
                                if (opt) setForm({ ...form, unit_kind: opt.value });
                              }}
                            />
                          </div>
                        );
                      })()
                    )}
                  </>
                ) : null}
                <Select
                  label="Taxe"
                  options={TAX_CODE_OPTIONS.map((o) => o.label)}
                  value={TAX_CODE_OPTIONS.find((o) => o.value === form.tax_code)?.label || ""}
                  onChange={(value) => {
                    const opt = TAX_CODE_OPTIONS.find((o) => o.label === value);
                    if (opt) {
                      setForm({
                        ...form,
                        tax_code: opt.value,
                        tax_profile: opt.value === "STANDARD_QC" ? "qc_standard" : "qc_zero",
                      });
                    }
                  }}
                />
                {form.product_type === "data_processing" && (
                  <div>
                    <Input
                      label="Minimum de facturation (CAD)"
                      type="number"
                      min={0}
                      step={0.01}
                      placeholder="ex. 150"
                      value={form.minimum_charge != null ? String(form.minimum_charge) : ""}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        setForm({ ...form, minimum_charge: v === "" ? null : parseFloat(v) || null });
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {form.product_type === "produit" && (
              <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-3">
                <div className="flex items-center gap-2 border-b border-border pb-2">
                  <h3 className="text-sm font-semibold text-text">Classification</h3>
                </div>
                <div>
                  <label className="text-sm text-muted mb-2 block">Catégories</label>
                  <div className="flex flex-wrap gap-2 min-h-[40px] rounded-md border border-border bg-surface px-3 py-2">
                    {categoriesList.map((cat) => {
                      const selected = form.category_ids.includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            setForm({
                              ...form,
                              category_ids: selected
                                ? form.category_ids.filter((id) => id !== cat.id)
                                : [...form.category_ids, cat.id],
                            });
                          }}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected ? "bg-primary/15 border-primary text-primary" : "border-border bg-surface-muted/50 text-muted hover:border-primary/50"}`}
                        >
                          {cat.name}
                        </button>
                      );
                    })}
                    {categoriesList.length === 0 && <span className="text-muted text-sm">Chargement…</span>}
                  </div>
                </div>
              </div>
            )}
            <div className="rounded-md border border-border bg-surface-muted/30 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-text border-b border-border pb-2">Opérationnel</h3>
              <div className="grid gap-3 md:grid-cols-2">
                <Select
                  label="Statut"
                  options={STATUS_OPTIONS.map((opt) => opt.label)}
                  value={STATUS_OPTIONS.find((opt) => opt.value === form.status)?.label || ""}
                  onChange={(value) => {
                    const option = STATUS_OPTIONS.find((opt) => opt.label === value);
                    if (option) setForm({ ...form, status: option.value });
                  }}
                />
              </div>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
}
